import React, { lazy, Suspense, useRef } from "react";

import UseQueryWrapper from "test/helpers/useQueryWrapper";
import { useViewerQuery } from "graphql/queries/Viewer.generated";

import StudentOnboardingFlow from "pages/StudentOnboardingFlow";

import { useIdentifyUser, identifyUser } from "utils/analytics";
import { AnalyticsProvider } from "utils/analytics/AnalyticsContext";
import { SurfaceType } from "utils/analytics/analyticsConstants";

import SunnySpinner from "ui/SunnySpinner";
import { CookieSettingsProvider } from "components/CookieSettings";
import resetCookieSettings from "utils/resetCookieSettings";
import GuestApp from "pages/GuestApp";
import { Router } from "ui/router/Router";
import { useAuthContext } from "graphql/AuthContext";
import AnalyticsRouteWrapper from "utils/analytics/AnalyticsRouteWrapper";
import {
  CodeOfConduct,
  UserAgreement,
  PrivacyPolicy,
  StudentPrivacyNotice,
} from "pages/Policies";

const TeacherApp = lazy(
  () => import(/* webpackChunkName: "page_teacher_app" */ "pages/TeacherApp"),
);

const StudentApp = lazy(
  () => import(/* webpackChunkName: "page_student_app" */ "pages/StudentApp"),
);

function getUserApp(userType: string): JSX.Element {
  switch (userType) {
    case "Teacher":
      return (
        <CookieSettingsProvider>
          <TeacherApp />
        </CookieSettingsProvider>
      );
    case "Student":
      return <StudentApp />;
    default:
      return <div>Unknown User type</div>;
  }
}

function Application() {
  const { currentUser } = useAuthContext();
  const isUserIdentified = useRef(false);

  useIdentifyUser(currentUser);

  if (!currentUser) {
    // not logged in
    return (
      <CookieSettingsProvider>
        <GuestApp />
      </CookieSettingsProvider>
    );
  }

  // Call `identify` as soon as user context is available
  // It is possible for event listeners to fire before useEffect updates
  // (such as <VisibilitySensor> if delayedCall is false)
  if (!isUserIdentified.current && currentUser) {
    identifyUser(currentUser.id, currentUser.type, currentUser.email);
    isUserIdentified.current = true;
  }

  resetCookieSettings(currentUser);

  if (!currentUser.hasAcceptedTos && currentUser.type === "Student") {
    const routes = [
      {
        index: true,
        element: (
          <AnalyticsRouteWrapper
            analyticsPageName="viewed_student_onboarding_page"
            analyticsSurface={SurfaceType.STUDENT_ONBOARDING_PAGE}
          >
            <AnalyticsProvider surface={SurfaceType.STUDENT_ONBOARDING_PAGE}>
              <StudentOnboardingFlow />
            </AnalyticsProvider>
          </AnalyticsRouteWrapper>
        ),
      },
      {
        path: "privacy-policy",
        element: (
          <AnalyticsRouteWrapper analyticsPageName="clicked_privacy_policy_link">
            <PrivacyPolicy />
          </AnalyticsRouteWrapper>
        ),
      },
      {
        path: "student-privacy-notice",
        element: (
          <AnalyticsRouteWrapper analyticsPageName="clicked_student_privacy_notice_link">
            <StudentPrivacyNotice />
          </AnalyticsRouteWrapper>
        ),
      },
      {
        path: "user-agreement",
        element: (
          <AnalyticsRouteWrapper analyticsPageName="clicked_user_agreement_link">
            <UserAgreement />
          </AnalyticsRouteWrapper>
        ),
      },
      {
        path: "code-of-conduct",
        element: (
          <AnalyticsRouteWrapper analyticsPageName="clicked_code_of_conduct_link">
            <CodeOfConduct />
          </AnalyticsRouteWrapper>
        ),
      },
    ];

    return <Router routes={routes} />;
  }

  try {
    // nextPath is set in the GuestApp when the user hits the fallback route that sends them to login
    const nextPath = sessionStorage.getItem("nextPath");
    if (nextPath) {
      sessionStorage.removeItem("nextPath");

      const noRedirectPathRegexes = [
        // Allows `create` to pass through, but no other reflection route
        /\/reflection\/(?!(?:create$))/,
        /\/sessions\/edit.*/,
      ];

      // Redirect only when it's not a disallowed path
      if (
        !noRedirectPathRegexes.some((pathRegex) => nextPath.match(pathRegex))
      ) {
        // `navigate` doesn't work here since there isn't a router in the component tree, yet.
        window.location.href = nextPath;
        return null;
      }
    }
  } catch (error) {
    // It's possible the user has storage disabled, so quietly allow this to fail.
  }

  return (
    // UseQueryWrapper to make sure useViewerQuery is executed & cached for future "useViewer" call
    // TODO: possibly write the currentUser result to the useViewer cache directly
    <UseQueryWrapper
      useQuery={useViewerQuery}
      queryOptions={{ nextFetchPolicy: "cache-first" }}
    >
      <Suspense fallback={<SunnySpinner fullPage={true} />}>
        {getUserApp(currentUser.type)}
      </Suspense>
    </UseQueryWrapper>
  );
}

export default Application;
